import React, { useEffect, useRef, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { db } from '../../services/firebase';
import type { OjtMinipageToken, OjtMinipageKind } from '../../types';

// --- Path-prefix helpers ---

export const MINIPAGE_PATH_PREFIX = '/m/';

/** Returns true when window.location.pathname is a mini-page URL. */
export function isMinipagePath(pathname: string): boolean {
  return pathname.startsWith(MINIPAGE_PATH_PREFIX);
}

/** Extracts the token id from a `/m/{token}` pathname. Returns null if invalid. */
export function extractMinipageToken(pathname: string): string | null {
  if (!isMinipagePath(pathname)) return null;
  const tail = pathname.slice(MINIPAGE_PATH_PREFIX.length);
  const token = tail.split('/')[0]?.split('?')[0]?.split('#')[0];
  return token && token.length > 0 ? token : null;
}

// --- Top-level route component ---

type LoadState =
  | { status: 'loading' }
  | { status: 'invalid'; reason: 'no-token' | 'not-found' | 'expired' | 'consumed' | 'error'; detail?: string }
  | { status: 'ready'; token: OjtMinipageToken }
  | { status: 'submitted' };

export const MinipageRoute: React.FC = () => {
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  useEffect(() => {
    const token = extractMinipageToken(window.location.pathname);
    if (!token) {
      setState({ status: 'invalid', reason: 'no-token' });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const ref = doc(db, 'soloready', 'data', 'ojt_minipage_tokens', token);
        const snap = await getDoc(ref);
        if (cancelled) return;
        if (!snap.exists()) {
          setState({ status: 'invalid', reason: 'not-found' });
          return;
        }
        const data = snap.data() as OjtMinipageToken;
        if (data.consumedAt != null) {
          setState({ status: 'invalid', reason: 'consumed' });
          return;
        }
        if (typeof data.expiresAt !== 'number' || data.expiresAt <= Date.now()) {
          setState({ status: 'invalid', reason: 'expired' });
          return;
        }
        setState({ status: 'ready', token: { ...data, token } });
      } catch (e) {
        if (cancelled) return;
        console.error('Failed to load mini-page token', e);
        // The token rule grants public read only when the doc is valid (not expired,
        // not consumed). A missing/expired/consumed token therefore returns
        // permission-denied — surface it to the user as "no longer valid" rather
        // than a generic error.
        const code = (e as { code?: string })?.code;
        if (code === 'permission-denied') {
          setState({ status: 'invalid', reason: 'not-found' });
        } else {
          setState({ status: 'invalid', reason: 'error', detail: (e as Error).message });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen bg-aero-50 flex items-start sm:items-center justify-center p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-lg border border-aero-100 overflow-hidden">
        {state.status === 'loading' && <LoadingCard />}
        {state.status === 'invalid' && <InvalidCard reason={state.reason} detail={state.detail} />}
        {state.status === 'submitted' && <ThanksCard />}
        {state.status === 'ready' && (
          <MinipageForm
            token={state.token}
            onSubmitted={() => setState({ status: 'submitted' })}
          />
        )}
      </div>
    </div>
  );
};

// --- Sub-components ---

const LoadingCard: React.FC = () => (
  <div className="p-8 text-center text-aero-600">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-aero-600 mx-auto" />
    <p className="mt-4 text-sm">Loading…</p>
  </div>
);

const InvalidCard: React.FC<{ reason: string; detail?: string }> = ({ reason, detail }) => {
  const message =
    reason === 'expired' ? 'This link has expired.' :
    reason === 'consumed' ? 'This link has already been used.' :
    reason === 'not-found' ? 'This link is no longer valid.' :
    reason === 'no-token' ? 'No token in URL.' :
    'Something went wrong loading this page.';
  return (
    <div className="p-8 text-center">
      <h1 className="text-lg font-bold text-aero-900">Mini-page unavailable</h1>
      <p className="mt-2 text-sm text-aero-600">{message}</p>
      {detail && <p className="mt-2 text-xs text-aero-400">{detail}</p>}
      <p className="mt-6 text-xs text-aero-400">
        If you were expecting to fill in a form, please check the most recent message you received.
      </p>
    </div>
  );
};

const ThanksCard: React.FC = () => (
  <div className="p-8 text-center">
    <h1 className="text-lg font-bold text-aero-900">Thanks!</h1>
    <p className="mt-2 text-sm text-aero-600">Your response has been recorded.</p>
    <p className="mt-6 text-xs text-aero-400">You can close this page.</p>
  </div>
);

const MinipageForm: React.FC<{
  token: OjtMinipageToken;
  onSubmitted: () => void;
}> = ({ token, onSubmitted }) => {
  return (
    <div>
      <header className="px-6 pt-6 pb-4 border-b border-aero-100">
        <p className="text-[10px] uppercase tracking-wider text-aero-400 font-bold">
          {token.forActor === 'lead_hand' ? 'For the lead hand' : 'For the candidate'}
        </p>
        <h1 className="mt-1 text-base font-bold text-aero-900 leading-snug">{token.prompt}</h1>
      </header>
      <div className="p-6">{renderFormForKind(token, onSubmitted)}</div>
    </div>
  );
};

function renderFormForKind(token: OjtMinipageToken, onSubmitted: () => void): React.ReactNode {
  switch (token.kind) {
    case 'free_text':
      return <FreeTextForm token={token} onSubmitted={onSubmitted} />;
    case 'objectives':
      return <StubForm kind="objectives" token={token} onSubmitted={onSubmitted} />;
    case 'wrap_up':
      return <StubForm kind="wrap_up" token={token} onSubmitted={onSubmitted} />;
    case 'selfie':
      return <StubForm kind="selfie" token={token} onSubmitted={onSubmitted} />;
    case 'sign_off':
      return <StubForm kind="sign_off" token={token} onSubmitted={onSubmitted} />;
    case 'custom':
      return <StubForm kind="custom" token={token} onSubmitted={onSubmitted} />;
  }
}

// --- free_text — implemented (proof of concept) ---

const FreeTextForm: React.FC<{
  token: OjtMinipageToken;
  onSubmitted: () => void;
}> = ({ token, onSubmitted }) => {
  const [text, setText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const recognitionRef = useRef<any>(null);

  const startRecording = () => {
    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setError('Voice input not supported in this browser. Please type your reply.');
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.onresult = (e: any) => {
      const t = Array.from(e.results)
        .map((r: any) => r[0].transcript)
        .join('');
      setText(t);
    };
    recognition.start();
    recognitionRef.current = recognition;
    setIsRecording(true);
  };

  const stopRecording = () => {
    recognitionRef.current?.stop();
    setIsRecording(false);
  };

  const submit = async () => {
    if (!text.trim()) return;
    setIsSubmitting(true);
    setError('');
    try {
      const functions = getFunctions(getApp(), 'europe-west2');
      const submitMinipage = httpsCallable(functions, 'submitMinipage');
      await submitMinipage({ token: token.token, response: { text: text.trim() } });
      onSubmitted();
    } catch (e: any) {
      console.error('submitMinipage failed', e);
      const code = e?.code || '';
      const msg =
        code === 'functions/already-exists' ? 'This link has already been used.' :
        code === 'functions/deadline-exceeded' ? 'This link has expired.' :
        code === 'functions/not-found' ? 'This link is no longer valid.' :
        'Could not submit. Please try again.';
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={6}
        placeholder="Type your reply…"
        className="w-full text-sm border border-aero-200 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-aero-500 resize-none"
      />
      <button
        type="button"
        onClick={isRecording ? stopRecording : startRecording}
        className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-colors ${
          isRecording ? 'bg-red-500 text-white' : 'bg-aero-100 text-aero-700 hover:bg-aero-200'
        }`}
      >
        {isRecording ? 'Stop recording' : 'Use voice instead'}
      </button>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <button
        type="button"
        onClick={submit}
        disabled={!text.trim() || isSubmitting}
        className="w-full py-3 bg-aero-600 hover:bg-aero-700 disabled:bg-aero-200 text-white rounded-lg font-semibold text-sm transition-colors"
      >
        {isSubmitting ? 'Submitting…' : 'Send reply'}
      </button>
    </div>
  );
};

// --- Stubs for other kinds ---

const StubForm: React.FC<{
  kind: OjtMinipageKind;
  token: OjtMinipageToken;
  onSubmitted: () => void;
}> = ({ kind, token, onSubmitted }) => {
  // TODO Phase 2/3 — implement objectives / wrap_up / selfie / sign_off / custom kinds.
  // The trainingroom runtime will format the prompt above; this stub posts an empty
  // confirmation payload via submitMinipage so the agent state machine can advance.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setIsSubmitting(true);
    setError('');
    try {
      const functions = getFunctions(getApp(), 'europe-west2');
      const submitMinipage = httpsCallable(functions, 'submitMinipage');
      await submitMinipage({ token: token.token, response: { stub: true, kind } });
      onSubmitted();
    } catch (e: any) {
      console.error('submitMinipage failed', e);
      setError(e?.message || 'Could not submit.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-xs text-amber-800">
        <strong>Coming soon —</strong> the <code className="font-mono">{kind}</code> mini-page
        is not yet implemented. Tap "Confirm" to acknowledge and let the agent move on.
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <button
        type="button"
        onClick={submit}
        disabled={isSubmitting}
        className="w-full py-3 bg-aero-600 hover:bg-aero-700 disabled:bg-aero-200 text-white rounded-lg font-semibold text-sm transition-colors"
      >
        {isSubmitting ? 'Submitting…' : 'Confirm'}
      </button>
    </div>
  );
};

export default MinipageRoute;
